'use client';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
} from '@/components/ui/breadcrumb';
import { useRouter } from 'next/navigation';
import { PiArrowLeftLight } from 'react-icons/pi';
import { generateBreadcrumbStructuredData } from '@/lib/seo';

interface PostBreadcrumbsProps {
  lang: string;
  postTitle?: string; // Optional post title for individual post pages
}

const labels: Record<string, { posts: string }> = {
  en: { posts: 'Posts' },
  ru: { posts: 'Статьи' },
  // Add more languages here as needed
};

function getLabel(lang: string): { posts: string } {
  return labels[lang] || labels.en;
}

export default function PostBreadcrumbs({ lang, postTitle }: PostBreadcrumbsProps) {
  const router = useRouter();

  // Use the language prop directly since we removed URL params
  const getCurrentLanguage = () => {
    return lang;
  };

  const handleBackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Simple browser back navigation - matches user expectations
    if (typeof window !== 'undefined') {
      window.history.back();
    } else {
      // Fallback to posts page with appropriate language
      const postsUrl = lang === 'ru' ? '/posts/ru' : '/posts';
      router.push(postsUrl);
    }
  };

  // Breadcrumb URL based on current language
  const getBreadcrumbUrl = () => {
    return lang === 'ru' ? '/posts/ru' : '/posts';
  };

  // Generate breadcrumb items for structured data
  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: getLabel(getCurrentLanguage()).posts, url: getBreadcrumbUrl() },
  ];

  // Add current post to breadcrumbs if we're on a post page
  if (postTitle) {
    breadcrumbItems.push({ name: postTitle, url: '#' }); // Current page
  }

  const breadcrumbStructuredData = generateBreadcrumbStructuredData(breadcrumbItems);

  return (
    <div className='flex flex-row gap-2 items-center'>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />
      <button
        onClick={handleBackClick}
        className='hover:text-zinc-700 cursor-pointer p-1 rounded transition-colors'
        title='Go back'
        type='button'
      >
        <PiArrowLeftLight size={20} />
      </button>
      <Breadcrumb className='hidden sm:flex'>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              href={getBreadcrumbUrl()}
              className='2xl:text-xl'
              onClick={handleBackClick}
            >
              {getLabel(getCurrentLanguage()).posts}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
}
